import { AnalyticsService } from '@alison/analytics/client';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha, type Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { formatDuration } from '../../lib/dateUtils';
import { ELLIPSIS_SX } from './utils';

const DEFAULT_MAX_SIZE = 260;
const COPIED_ICON_MS = 1500;

const ASPECT_PATTERN = /(\d+)\s*:\s*(\d+)/;

const BADGE_SX = (theme: Theme) => ({
  px: 1,
  borderRadius: `${theme.radius.sm}px`,
  bgcolor: alpha(theme.palette.text.primary, 0.06),
  color: theme.palette.text.primary,
});

interface CreativePreviewProps {
  name: string;
  creativeType?: string | null;
  format: string | null;
  duration: number | null;
  thumbnailUrl: string;
  previewUrl: string;
  maxSize?: number;
  showCopyButton?: boolean;
  /** false renders the same muted, looping, controls-free autoplay used by the thumbnail's hover
   * preview, instead of a real player — for a popup that only appears while hovering. */
  controls?: boolean;
  /** false drops the badges and name under the media, for a host that shows them itself. */
  caption?: boolean;
}

function aspectRatioOf(format: string | null): number {
  const match = format === null ? null : ASPECT_PATTERN.exec(format);
  const width = Number(match?.[1]);
  const height = Number(match?.[2]);
  if (!width || !height) return 1;
  return width / height;
}

export function CreativePreview({
  name,
  creativeType = null,
  format,
  duration,
  thumbnailUrl,
  previewUrl,
  maxSize = DEFAULT_MAX_SIZE,
  showCopyButton = false,
  controls = true,
  caption = true,
}: CreativePreviewProps): ReactNode {
  const [copied, setCopied] = useState(false);
  const isImage = creativeType?.toLowerCase() === 'image';
  const playable = previewUrl !== '' && !isImage;

  const ratio = aspectRatioOf(format);
  const width = ratio >= 1 ? maxSize : Math.round(maxSize * ratio);
  const height = ratio >= 1 ? Math.round(maxSize / ratio) : maxSize;

  function handleCopy(): void {
    if (!navigator.clipboard) return;
    AnalyticsService.track('Creative Detail - Copy Preview');
    navigator.clipboard.writeText(name).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_ICON_MS);
    });
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        sx={{
          width,
          height,
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.page',
        }}
      >
        {playable ? (
          <Box
            component="video"
            src={previewUrl}
            controls={controls}
            autoPlay={!controls}
            muted={!controls}
            loop={!controls}
            playsInline
            poster={thumbnailUrl}
            preload="metadata"
            // A player shows the whole frame; the hover loop fills its box like the thumbnail.
            sx={{ width: '100%', height: '100%', objectFit: controls ? 'contain' : 'cover' }}
          />
        ) : (
          <Box
            component="img"
            src={thumbnailUrl}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </Box>
      {caption && (
        <>
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            {[format, formatDuration(duration)]
              .filter((badge) => badge !== null && badge !== '')
              .map((badge) => (
                <Typography key={badge} variant="overline" sx={BADGE_SX}>
                  {badge}
                </Typography>
              ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 2,
              minWidth: 0,
              maxWidth: maxSize,
            }}
          >
            <Tooltip title={name} placement="top">
              <Typography variant="h5" sx={{ ...ELLIPSIS_SX, minWidth: 0 }}>
                {name}
              </Typography>
            </Tooltip>
            {showCopyButton && (
              <Tooltip title={copied ? 'Copied' : 'Copy'} placement="top">
                <IconButton size="small" onClick={handleCopy}>
                  <ContentCopyOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}
